/**
 * Free-swatches page entry: wires browse/filter, cart/favorites, and detail
 * offcanvas modules together.
 */
import { createSwatchFilters } from './swatch-filters';
import { createSwatchCartFavorites } from './swatch-cart-favorites';
import { createSwatchDetail } from './swatch-detail';
import { readCatalog, readFavorites } from './swatch-catalog';
import { renderSwatchCard } from './swatch-card-render';

const SCROLL_STORAGE_KEY = 'graberSwatchScroll';

const initSwatchPage = () => {
  const app = document.getElementById('swatches-app');
  if (!app) {
    return;
  }

  const browseMode = document.getElementById('swatch-browse-mode');
  const filteredMode = document.getElementById('swatch-filtered-mode');
  const emptyState = document.getElementById('swatch-empty-state');
  const detailPanel = document.getElementById('swatch-detail-panel');
  const learnMoreModal = document.getElementById('swatch-learn-more-modal');
  const visualizeTileTemplate = document.getElementById('swatch-visualize-tile-template');
  const visualizeUrl = (app.getAttribute('data-visualize-url') || '').trim() || '#';

  const filteredCards = filteredMode
    ? Array.prototype.slice.call(filteredMode.querySelectorAll('.swatch-card-col'))
    : [];

  // Null when the island is absent or unparseable; filtering falls back to reading the cards.
  const catalog = readCatalog(document);
  const favorites = readFavorites(document);

  const state = {
    activeJump: '',
    filters: {
      color: [],
      solution: [],
      product: [],
      collection: [],
    },
    mode: 'browse',
    detailVariantCards: [],
    detailVariantIndex: -1,
    detailPrevNextCards: [],
    detailPrevNextIndex: -1,
  };

  // Logging in from the detail panel finishes with a full reload, which lands the user back
  // at the top of a 2000-card grid. The open panel itself is restored from `?detail=`, but
  // scroll offset has no business in a shareable URL, so it is handed over out of band.
  const saveScrollForReload = () => {
    try {
      window.sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
    } catch (e) {
      // Private browsing or a full quota; losing the offset is not worth failing the login.
    }
  };

  const consumeSavedScroll = () => {
    try {
      const raw = window.sessionStorage.getItem(SCROLL_STORAGE_KEY);
      window.sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      const offset = parseInt(raw, 10);
      return Number.isNaN(offset) ? null : offset;
    } catch (e) {
      return null;
    }
  };

  const ctx = {
    app,
    state,
    browseMode,
    filteredMode,
    filteredCards,
    catalog,
    favorites,
    emptyState,
    detailPanel,
    visualizeTileTemplate,
    visualizeUrl,
    filterBar: null,
    saveScrollForReload,
  };

  Object.assign(ctx, createSwatchFilters(ctx));
  Object.assign(ctx, createSwatchCartFavorites(ctx));
  Object.assign(ctx, createSwatchDetail(ctx));

  /**
   * Keeps the favorites island in step with what the server returned.
   *
   * Cards are rendered from this map long after it is read - the next chunk the grid renders, a
   * filter change that rebuilds the grid, and the cards the detail panel builds from catalog
   * records for prev/next. Without this they are built from the state the page arrived in, so a
   * heart the visitor just filled reverts on its own.
   *
   * Mutated in place: swatch-grid-render.js captured this object at construction.
   */
  ctx.onFavoriteFormPosted = ({ swatchId, nowFavorite }) => {
    const id = String(swatchId);

    if (!nowFavorite) {
      delete favorites[id];
      return;
    }

    // Searched from the document rather than from `app`, because the detail panel sits outside it
    // and may hold the only form for this swatch. The remove form always carries the asset id -
    // it is what the remove post sends - so there is nowhere else to read it from.
    const removeForm = `.swatch-favorite-form[data-favorite-action="remove"][data-swatch-id="${id}"]`;
    const assetInput = document.querySelector(`${removeForm} input[name="assetId"]`);

    if (assetInput) {
      favorites[id] = assetInput.value;
    }
  };

  // Read-only hook for tools/swatch-link-fixture. Kept deliberately narrow rather than exposing
  // ctx, so it stays an interface the resolver has to keep honouring rather than a window onto
  // whatever the page happens to hold.
  window.graberSwatchTest = {
    resolveColorIdentity: ctx.resolveColorIdentity,
    matchingIdsForFilters: ctx.matchingIdsForFilters,
    catalog,
    favorites,
    renderSwatchCard,
  };

  // -------------------------------------------------------------------------
  // Learn more modal
  // -------------------------------------------------------------------------

  const openLearnMoreModal = (title, productLineId) => {
    if (!learnMoreModal) {
      return;
    }
    const titleEl = learnMoreModal.querySelector('.swatch-learn-more-title');
    const bodyEl = learnMoreModal.querySelector('.swatch-learn-more-body');
    if (titleEl) {
      titleEl.textContent = title || 'Learn More';
    }

    if (bodyEl) {
      const source = productLineId
        ? document.getElementById(`swatch-learn-more-${productLineId}`)
        : null;
      const sourceHtml = source && source.innerHTML.trim() ? source.innerHTML.trim() : '';

      if (sourceHtml) {
        bodyEl.innerHTML = sourceHtml;
      } else {
        bodyEl.innerHTML = '<p class="swatch-learn-more-copy text-muted mb-0">Product details coming soon.</p>';
      }
    }

    if (window.bootstrap && window.bootstrap.Modal) {
      let instance = window.bootstrap.Modal.getInstance(learnMoreModal);
      if (!instance) {
        instance = new window.bootstrap.Modal(learnMoreModal);
      }
      instance.show();
      return;
    }

    learnMoreModal.classList.add('show', 'd-block');
    learnMoreModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
  };

  const closeLearnMoreModal = () => {
    if (!learnMoreModal) {
      return;
    }

    if (window.bootstrap && window.bootstrap.Modal) {
      const instance = window.bootstrap.Modal.getInstance(learnMoreModal);
      if (instance) {
        instance.hide();
        return;
      }
    }

    learnMoreModal.classList.remove('show', 'd-block');
    learnMoreModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
  };

  // -------------------------------------------------------------------------
  // Event wiring
  // -------------------------------------------------------------------------

  ctx.initFilterBar();
  ctx.bindDetailEvents();

  const toolbar = app.querySelector('.swatch-toolbar');
  const syncToolbarHeight = () => {
    if (!toolbar) {
      return;
    }
    // Drive scroll-margin on .swatch-tab-section so jump links clear the sticky stack.
    document.documentElement.style.setProperty(
      '--swatch-toolbar-height',
      `${toolbar.offsetHeight}px`,
    );
  };
  const syncToolbarStuck = () => {
    if (!toolbar) {
      return;
    }
    const headerHeight = parseFloat(document.documentElement.style.getPropertyValue('--header-height')) || 60;
    toolbar.classList.toggle('is-stuck', toolbar.getBoundingClientRect().top <= headerHeight + 1);
  };
  window.addEventListener('scroll', syncToolbarStuck, { passive: true });
  window.addEventListener('resize', () => {
    syncToolbarHeight();
    syncToolbarStuck();
  });
  if (toolbar && typeof window.ResizeObserver !== 'undefined') {
    new window.ResizeObserver(syncToolbarHeight).observe(toolbar);
  }
  syncToolbarHeight();
  syncToolbarStuck();

  Array.prototype.slice.call(app.querySelectorAll('.swatch-jump-link')).forEach((link) => {
    link.addEventListener('click', () => {
      const tabKey = link.getAttribute('data-tab');
      if (tabKey) {
        ctx.setJumpLinkActive(tabKey);
      }
    });
  });

  window.addEventListener('scroll', () => {
    ctx.syncJumpLinkFromScroll();
  }, { passive: true });

  app.addEventListener('click', (event) => {
    const seeAll = event.target.closest('.swatch-see-all');
    if (seeAll && ctx.filterBar) {
      const productLineId = seeAll.getAttribute('data-productline-id')
        || (seeAll.closest('.swatch-category-row')
          && seeAll.closest('.swatch-category-row').getAttribute('data-productline-id'));
      if (productLineId) {
        ctx.filterBar.setState({
          color: [],
          solution: [],
          product: [productLineId],
          collection: [],
        });
        // Filtered view replaces a long browse page; jump to top so results aren't off-screen.
        window.scrollTo(0, 0);
      }
      return;
    }

    const learnMoreBtn = event.target.closest('.swatch-learn-more-btn');
    if (learnMoreBtn) {
      openLearnMoreModal(
        learnMoreBtn.getAttribute('data-title'),
        learnMoreBtn.getAttribute('data-productline-id'),
      );
      return;
    }

    const viewDetail = event.target.closest('.swatch-view-detail');
    if (viewDetail) {
      const card = viewDetail.closest('.swatch-card-col');
      if (card) {
        ctx.openDetailForCard(card);
      }
      return;
    }

    const cartBtn = event.target.closest('.swatch-cart-btn');
    if (cartBtn) {
      ctx.onCartClick(cartBtn);
    }
  });

  app.addEventListener('submit', (event) => {
    if (event.target.closest('.swatch-favorite-form')) {
      ctx.onFavoriteSubmit(event);
    }
  });

  if (learnMoreModal) {
    Array.prototype.slice.call(learnMoreModal.querySelectorAll('.swatch-learn-more-close, [data-bs-dismiss="modal"]')).forEach((btn) => {
      btn.addEventListener('click', closeLearnMoreModal);
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeLearnMoreModal();
    }
  });

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) {
      clearTimeout(resizeTimer);
    }
    resizeTimer = setTimeout(() => {
      ctx.updateCategoryStrips();
      ctx.syncJumpLinkFromScroll();
      if (state.mode === 'filtered') {
        // Resize only needs the width-dependent parts redone. A full re-render would discard
        // every chunk the visitor has scrolled through and collapse the page under them, and
        // resize fires on phone rotation and whenever mobile browser chrome shows or hides.
        ctx.applyFilteredView({ preserveRendered: true });
      }
    }, 150);
  });

  // -------------------------------------------------------------------------
  // Initial state
  // -------------------------------------------------------------------------

  ctx.updateCategoryStrips();
  ctx.syncJumpLinkFromScroll();
  ctx.initCartState();

  // Filters from the URL have been applied by now, so the correct view is in place and the
  // placeholders can give way to it. Set by the inline script in SwatchPage/Index.cshtml.
  app.classList.remove('is-filter-booting');

  // Last, so filters from the URL have already been applied and the grid is at its final
  // height - otherwise a restored offset lands in the wrong place.
  const deepLinkedCard = ctx.openDetailFromUrl();
  const savedScroll = consumeSavedScroll();

  if (savedScroll !== null) {
    // The filtered grid renders in chunks, so straight after a login reload the document can be
    // far shorter than it was when the offset was saved - scrollTo would clamp to the bottom of
    // whatever is rendered and land the visitor somewhere they were never looking. Render forward
    // until the offset is reachable. Bounded, because the result set is finite and
    // hasMoreToRender goes false at the end of it.
    if (typeof ctx.hasMoreToRender === 'function' && typeof ctx.renderMoreCards === 'function') {
      while (ctx.hasMoreToRender()
        && document.documentElement.scrollHeight < savedScroll + window.innerHeight) {
        if (ctx.renderMoreCards() <= 0) {
          break;
        }
      }
    }

    window.scrollTo({ top: savedScroll, behavior: 'auto' });
  } else if (deepLinkedCard) {
    // Arrived on a shared link rather than a login round trip, so put the swatch in view.
    deepLinkedCard.scrollIntoView({ block: 'center', behavior: 'auto' });
  }

  // Only set the generic Visualize landing URL on browse CTAs. Filtered
  // product-group tiles and the detail button use product/color deep links.
  const genericVisualizeLinks = app.querySelectorAll('.swatch-browse-visualize-cta');
  Array.prototype.slice.call(genericVisualizeLinks).forEach((link) => {
    if (visualizeUrl) {
      link.setAttribute('href', visualizeUrl);
    }
  });
};

export default initSwatchPage;
export { initSwatchPage };
